import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FieldSet,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Paragraph,
  Radio,
  TextButton,
} from '@zillow/constellation-10';

import { ForRentByType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_forRentBy } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { OWNERSHIP_VERIFICATION_STATE_TAG } from '../../../hooks/queries/useOwnershipVerification';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import {
  checkDisplayForRentByChangeWarning,
  checkIsApplicationAcceptedValueChanging,
  getRoomForRentFlow,
} from '../../../utils/roomForRentUtils';

import { wizardHeadingStyles } from '../../../styles/wizard';

import ForRentByWarning from './ForRentByWarning';

const inputName = 'forRentBy';
const permissionInputName = 'hasPermissionToCreateListing';
const RoomForRentByFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const formMode = useFormMode();

  const initialForRentBy = listingDetails?.forRentByType ?? ForRentByType.OWNER;
  const initialHasPermission = listingDetails?.roomForRent?.hasPermissionToCreateListing ?? false;

  const [showWarning, setShowWarning] = useState(false);
  const [pendingForRentByValue, setPendingForRentByValue] = useState<ForRentByType | null>(null);

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_forRentBy,
    meta: {
      invalidates: [OWNERSHIP_VERIFICATION_STATE_TAG],
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_forRentBy' },
    },
  });

  const permissionMutation = useMultiEditMutation();

  const forRentByField = useFieldSave({
    fieldName: inputName,
    initialValue: initialForRentBy,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        listingAlias,
        forRentBy: value as ForRentByType,
      },
    }),
    autoSaveOnChange: true,
  });

  const permissionField = useFieldSave({
    fieldName: permissionInputName,
    initialValue: initialHasPermission,
    mutation: permissionMutation,
    buildMutationVariables: (value) => ({
      postData: {
        property: {
          listingAlias,
          roomForRent: {
            hasPermissionToCreateListing: value,
          },
        },
      },
    }),
    autoSaveOnChange: true,
    enabled: forRentByField.value === ForRentByType.TENANT,
  });

  // Validate permission field when TENANT is selected without permission
  useEffect(() => {
    if (forRentByField.value === ForRentByType.TENANT && !permissionField.value) {
      // Trigger validation by calling onBlur with current (false) value
      // This will cause useFieldSave to run validation and report the error
      permissionField.onBlur?.();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [forRentByField.value, permissionField.value]);

  const handleForRentByChange = (value: string) => {
    setShowWarning(false);

    const roomForRent = listingDetails?.roomForRent ?? {
      currentRoommatesDetails: {},
      roommateRequirements: {},
    };
    const isApplicationsAccepted = listingDetails?.isApplicationsAccepted ?? false;

    const currentFlow = getRoomForRentFlow(forRentByField.value);
    const nextFlow = getRoomForRentFlow(value as ForRentByType);

    // Check for flow change with data loss
    const { displayWarning: hasDataLoss } = checkDisplayForRentByChangeWarning({
      roomForRentFlow: currentFlow,
      roomForRent,
    });

    // Check for applications status change
    const { isApplicationAcceptedValueChanging } = checkIsApplicationAcceptedValueChanging({
      isApplicationsAccepted,
      nextForRentByVal: value as ForRentByType,
    });

    const shouldWarn = (currentFlow !== nextFlow && hasDataLoss) || isApplicationAcceptedValueChanging;

    if (shouldWarn) {
      setShowWarning(true);
      setPendingForRentByValue(value as ForRentByType);

      // Skip update until warning modal is confirmed
      if (!isInModalMode) {
        return;
      }
    }

    // No warning needed, proceed normally
    // If changing away from tenant, clear permission
    if (value !== ForRentByType.TENANT) {
      permissionField.onChange(false);
    }

    forRentByField.onChange(value as ForRentByType);
  };

  const handleWarningModalConfirm = () => {
    if (!pendingForRentByValue) return;

    // If changing away from tenant, clear permission
    if (pendingForRentByValue !== ForRentByType.TENANT) {
      permissionField.onChange(false);
    }

    forRentByField.onChange(pendingForRentByValue);
    setShowWarning(false);
    setPendingForRentByValue(null);
  };

  const isInModalMode = formMode.mode === 'modal';

  const roomForRent = listingDetails?.roomForRent ?? {
    currentRoommatesDetails: {},
    roommateRequirements: {},
  };
  const isApplicationsAccepted = listingDetails?.isApplicationsAccepted ?? false;

  return (
    <Box data-testid="RoomForRentByFormField">
      <FieldSet
        legend={<Legend>Listed by</Legend>}
        required={true}
        error={!!forRentByField.error}
        description={forRentByField.error && <FormHelp>{forRentByField.error}</FormHelp>}
      >
        <Box css={{ display: 'flex', paddingLeft: '200', flexDirection: 'column' }}>
          <LabeledControl
            label={<Label>I’m the property owner/manager, and I don’t live on the property</Label>}
            control={
              <Radio
                name={inputName}
                value={ForRentByType.OWNER}
                checked={forRentByField.value === ForRentByType.OWNER}
                onChange={(e) => handleForRentByChange(e.target.value)}
              />
            }
          />

          <LabeledControl
            label={<Label>I’m the property owner/manager, and I do live on the property</Label>}
            control={
              <Radio
                name={inputName}
                value={ForRentByType.OWNER_RESIDENT}
                checked={forRentByField.value === ForRentByType.OWNER_RESIDENT}
                onChange={(e) => handleForRentByChange(e.target.value)}
              />
            }
          />

          <LabeledControl
            label={<Label>I’m a current tenant living on the property</Label>}
            control={
              <Radio
                name={inputName}
                value={ForRentByType.TENANT}
                checked={forRentByField.value === ForRentByType.TENANT}
                onChange={(e) => handleForRentByChange(e.target.value)}
              />
            }
          />

          <LabeledControl
            label={<Label>I’m an agent/broker/management company, and don’t live on the property</Label>}
            control={
              <Radio
                name={inputName}
                value={ForRentByType.AGENT}
                checked={forRentByField.value === ForRentByType.AGENT}
                onChange={(e) => handleForRentByChange(e.target.value)}
              />
            }
          />
        </Box>
      </FieldSet>

      {forRentByField.value === ForRentByType.TENANT && (
        <>
          <Box data-testid="PermissionFromPropertyOwnerSection" css={{ marginBottom: '800', marginTop: '1200' }}>
            <FieldSet
              legend={
                <>
                  <Legend css={{ ...wizardHeadingStyles, textStyle: 'heading-sm' }}>
                    Do you have permission from the property owner/manager?
                  </Legend>
                  <Paragraph>You must-have the owner/manager’s permission to create this listing.</Paragraph>{' '}
                </>
              }
              required={true}
              error={!!permissionField.error}
              description={permissionField.error && <FormHelp>{permissionField.error}</FormHelp>}
            >
              <LabeledControl
                label={
                  <Label>
                    By checking this box, I confirm I have authority by way of applicable law, an executed lease, or the
                    property owner’s explicit consent to publish this listing on Zillow’s platform.
                  </Label>
                }
                control={
                  <Checkbox
                    name={permissionInputName}
                    checked={permissionField.value}
                    onChange={(e) => permissionField.onChange(e.target.checked)}
                  />
                }
              />
            </FieldSet>
          </Box>
        </>
      )}

      {showWarning && pendingForRentByValue && isInModalMode && (
        <Alert tone="warning" css={{ marginTop: '800' }}>
          <ForRentByWarning
            roomForRentFlow={getRoomForRentFlow(forRentByField.value)}
            nextForRentByVal={pendingForRentByValue}
            roomForRent={roomForRent}
            isApplicationsAccepted={isApplicationsAccepted}
          />
        </Alert>
      )}

      {showWarning && pendingForRentByValue && !isInModalMode && (
        <Modal
          size="sm"
          open={true}
          onOpenChange={(open) => !open && setShowWarning(false)}
          body={
            <>
              <Heading level={2} textStyle="heading-sm" css={{ marginBottom: '400' }}>
                Change who’s listing this property?
              </Heading>
              <ForRentByWarning
                roomForRentFlow={getRoomForRentFlow(forRentByField.value)}
                nextForRentByVal={pendingForRentByValue}
                roomForRent={roomForRent}
                isApplicationsAccepted={isApplicationsAccepted}
              />
            </>
          }
          footer={
            <ButtonGroup aria-label="Change selection actions">
              <TextButton onClick={() => setShowWarning(false)}>No, don&apos;t change</TextButton>
              <Button emphasis="filled" onClick={handleWarningModalConfirm}>
                Yes, change selection
              </Button>
            </ButtonGroup>
          }
        />
      )}
    </Box>
  );
};

export default RoomForRentByFormField;
